import NiceSelect from 'nice-select2';
import { PLPhoneFormatter } from '../../../assets/scripts/extensions/phoneFormatter/PLPhoneFormatter.js';
import { postalFormatter } from '../../../assets/scripts/extensions/postalFormatter/postalFormatter.js';
import { initFileInput } from '../../../assets/scripts/extensions/fileInput/fileInput.js';
import flatpickr from 'flatpickr';
import { Polish } from 'flatpickr/dist/l10n/pl.js';

export function initForm() {

    /* ==============================
     * Datepicker
     * ============================== */
    flatpickr('#purhase_date', {
        locale: Polish,
        dateFormat: 'Y-m-d',
        maxDate: 'today',
    });

    /* ==============================
     * Fileinput
     * ============================== */
    initFileInput('purhase_file');

    /* ==============================
     * NiceSelect
     * ============================== */
    const initNiceSelect = (select) => {
        if (!select) return;
        if (select.dataset.niceInitialized) return;

        select.dataset.niceInitialized = '1';

        NiceSelect.bind(select, {
            searchable: false,
            searchtext: 'Szukaj…',
            selectedtext: ' wybrano',
            placeholder: 'Wybierz',
        });
    };

    const initAllNiceSelects = (root = document) => {
        root.querySelectorAll('select.js-nice').forEach(initNiceSelect);
    };

    /* ==============================
     * AJAX helpers
     * ============================== */
    function disableWithLoader(button) {
        button.disabled = true;
        button.style.opacity = '0.6';

        if (!button.querySelector('.loader')) {
            const loader = document.createElement('span');
            loader.className = 'loader';
            loader.style.marginLeft = '8px';
            button.appendChild(loader);
        }
    }

    function enableAndRemoveLoader(button) {
        button.disabled = false;
        button.style.opacity = '';
        const loader = button.querySelector('.loader');
        if (loader) loader.remove();
    }

    /* ==============================
     * FORM MESSAGE
     * ============================== */
    function executeRecaptcha(action) {
        return new Promise((resolve, reject) => {
            if (typeof grecaptcha === 'undefined') {
                reject(new Error('grecaptcha not loaded'));
                return;
            }

            grecaptcha.ready(() => {
                grecaptcha.execute(recaptchaSiteKey, { action: action })
                    .then(token => resolve(token))
                    .catch(err => reject(err));
            });
        });
    }

    function getFormMessageBox(form) {
        return form.parentElement?.querySelector('.form-message') || null;
    }

    function getNiceSelectElement(select) {
        if (!select.classList.contains('js-nice')) return null;

        // NiceSelect tworzy element zaraz PO select
        const next = select.nextElementSibling;
        if (next && next.classList.contains('nice-select')) {
            return next;
        }

        return null;
    }

    function setFormMessage(form, message, type = 'error') {
        const box = getFormMessageBox(form);
        if (!box) return;

        box.textContent = message;
        box.setAttribute('role', 'alert');
        box.className = 'form-message';
        box.classList.add(`form-message--${type}`);
    }

    /* ==============================
     * WALIDACJA
     * ============================== */
    function getFieldWrapper(field) {
        if (field.type === 'checkbox') {
            return field.closest('label');
        }
        return field.closest('div');
    }

    function clearValidationErrors(form) {
        form.querySelectorAll('[aria-invalid="true"]').forEach(el => {
            el.removeAttribute('aria-invalid');
        });

        form.querySelectorAll('.form__error').forEach(error => error.remove());

        // 🔹 usuń błędy checkboxów (klasa na label)
        form.querySelectorAll('.has-error').forEach(el => {
            el.classList.remove('has-error');
        });

        form.querySelectorAll('.form__file-label.error').forEach(el => {
            el.classList.remove('error');
        });

        const box = getFormMessageBox(form);
        if (box) {
            box.textContent = '';
            box.className = 'form-message';
            box.removeAttribute('role');
        }
    }

    function handleValidationErrors(form, errors) {
        console.log(errors);

        if (errors['g-recaptcha-response']) {
            setFormMessage(
                form,
                errors['g-recaptcha-response'][0] || 'Nie udało się potwierdzić, że nie jesteś robotem.'
            );

            return;
        }

        setFormMessage(
            form,
            'Formularz zawiera błędy. Sprawdź zaznaczone pola.'
        );

        Object.keys(errors).forEach(fieldName => {
            const field = form.querySelector(`#${CSS.escape(fieldName)}`);
            if (!field) return;

            const wrapper = getFieldWrapper(field);
            if (!wrapper) return;

            field.setAttribute('aria-invalid', 'true');

            if (field.type === 'checkbox') {
                wrapper.classList.add('has-error');
                return;
            }

            if (field.type === 'file') {
                const fileLabel = field
                    .closest('.form__file')
                    ?.querySelector('.form__file-label');

                if (fileLabel) {
                    fileLabel.classList.add('error');
                }
            }

            if (field.tagName === 'SELECT') {
                const nice = getNiceSelectElement(field);

                if (nice) {
                    nice.setAttribute('aria-invalid', 'true');
                }
            }

            if (wrapper.querySelector('.form__error')) return;

            const errorEl = document.createElement('div');
            errorEl.className = 'form__error';
            errorEl.setAttribute('role', 'alert');
            errorEl.textContent = errors[fieldName][0];

            wrapper.appendChild(errorEl);
        });
    }

    function initFormSubmit(form) {
        if (!form) {
            return;
        }

        let isSubmitting = false;

        form.addEventListener('submit', async (event) => {
            event.preventDefault();

            if (isSubmitting) {
                return;
            }

            clearValidationErrors(form);

            const submitButton = form.querySelector('[type="submit"]');

            if (!submitButton) {
                return;
            }

            isSubmitting = true;
            disableWithLoader(submitButton);

            try {
                /*
                 * reCAPTCHA
                 */
                let recaptchaToken = null;

                if (typeof recaptchaSiteKey !== 'undefined' && recaptchaSiteKey) {
                    recaptchaToken = await executeRecaptcha(form.getAttribute('action'));
                }

                /*
                 * FormData
                 */
                const formData = new FormData(form);

                if (recaptchaToken) {
                    formData.set('g-recaptcha-response', recaptchaToken);
                }

                /*
                 * Request
                 */
                const response = await fetch(form.action, {
                    method: 'POST',
                    body: formData,
                    headers: {
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                });

                /*
                 * JSON response
                 */
                let data = {};

                try {
                    data = await response.json();
                } catch (error) {
                    // Backend zwrócił niepoprawną odpowiedź JSON.
                }

                /*
                 * Validation error
                 */
                if (response.status === 422) {
                    handleValidationErrors(
                        form,
                        data.errors || {}
                    );

                    return;
                }

                /*
                 * CSRF
                 */
                if (response.status === 419) {
                    setFormMessage(
                        form,
                        'Sesja wygasła. Odśwież stronę i spróbuj ponownie.'
                    );

                    return;
                }

                /*
                 * Server error
                 */
                if (!response.ok) {
                    setFormMessage(
                        form,
                        data.message || 'Wystąpił błąd serwera. Spróbuj ponownie za chwilę.'
                    );

                    return;
                }

                /*
                 * Success
                 */
                resetForm(form);

                setFormMessage(
                    form,
                    data.message || 'Formularz został wysłany pomyślnie. Nastąpi przekierowanie.',
                    'success'
                );

                const redirectUrl = form.dataset.redirect;

                if (redirectUrl) {
                    setTimeout(() => {
                        window.location.assign(redirectUrl);
                    }, 1000);

                    return;
                }

            } catch (error) {
                console.error('Form submission error:', error);

                setFormMessage(
                    form,
                    'Nie udało się wysłać formularza. Sprawdź połączenie z internetem i spróbuj ponownie.'
                );
            } finally {
                isSubmitting = false;
                enableAndRemoveLoader(submitButton);
            }
        });
    }

    function resetForm(form) {
        form.reset();

        /*
         * Flatpickr
         */
        const dateInput = form.querySelector('#purhase_date');

        if (dateInput && dateInput._flatpickr) {
            dateInput._flatpickr.clear();
        }

        /*
         * File input
         */
        const fileInput = form.querySelector('#purhase_file');

        if (fileInput) {
            fileInput.value = '';

            const fileName = fileInput
                .closest('.form__file')
                ?.querySelector('.form__file-name');

            if (fileName) {
                fileName.textContent = 'Nie wybrano pliku';
            }
        }

        /*
         * NiceSelect
         */
        form.querySelectorAll('select.js-nice').forEach(select => {
            select.value = '';

            const niceSelect = getNiceSelectElement(select);

            if (niceSelect) {
                niceSelect.classList.remove('open');
                niceSelect.setAttribute('aria-invalid', 'false');
            }

            select.dispatchEvent(new Event('change', {
                bubbles: true,
            }));
        });

        /*
         * Validation state
         */
        clearValidationErrors(form);
    }


    /* ==============================
     * Formattery
     * ============================== */
    PLPhoneFormatter('#phone');
    postalFormatter('#post_code');

    /* ==============================
     * INIT
     * ============================== */
    initAllNiceSelects();

    const form = document.querySelector('.form--oszczedzaj-z-salus');

    if (form) {
        initFormSubmit(form);
    }
}
